import React from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { SalonDashboardReport } from '../../../services/reportService';
import { StatTile, PersonAvatar } from '../../../components/ui';
import { tokens } from '../../../theme';
import { DateRange } from './StatsDateRange';
import {
  SectionCard,
  CHART_COLORS,
  formatPLN,
  formatNumber,
  EmptyHint,
  StatTileGrid,
  StatsRow,
} from './statsShared';
import { DonutChart } from './ChartKit';

interface Props {
  salonId: string;
  range: DateRange;
  data: SalonDashboardReport;
}

const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

export const ClientsTab: React.FC<Props> = ({ data }) => {
  const c = data.clients;
  const donut = [
    { name: 'Nowi', value: c.new, color: CHART_COLORS[0] },
    { name: 'Powracający', value: c.returning, color: CHART_COLORS[1] },
  ];

  return (
    <Stack gap={2}>
      <StatTileGrid>
        <StatTile label="Klienci" value={formatNumber(c.total)} />
        <StatTile label="Nowi" value={formatNumber(c.new)} />
        <StatTile label="Powracający" value={formatNumber(c.returning)} />
        <StatTile label="Średni wydatek" value={formatPLN(c.avgSpend)} hint="Na klienta w wybranym okresie" />
      </StatTileGrid>

      <StatsRow template="1fr 2fr">
        <SectionCard title="Nowi i powracający">
          {c.total > 0 ? <DonutChart data={donut} currency={false} /> : <EmptyHint>Brak klientów w wybranym okresie</EmptyHint>}
        </SectionCard>
        <SectionCard title="Najlepsi klienci" subtitle="Według wydanej kwoty" padding={c.topClients.length ? 'none' : 'normal'}>
          {c.topClients.length ? (
            <TableContainer>
              <Table sx={{ minWidth: 440 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }}>Lp.</TableCell>
                    <TableCell>Klient</TableCell>
                    <TableCell align="right">Wizyty</TableCell>
                    <TableCell align="right">Wydane</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {c.topClients.map((cl, i) => (
                    <TableRow key={cl.id} hover>
                      <TableCell sx={{ ...NUM, color: tokens.muted }}>{i + 1}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1.25}>
                          <PersonAvatar firstName={cl.firstName} lastName={cl.lastName} seed={cl.id} size={32} />
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {cl.firstName} {cl.lastName}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={NUM}>{formatNumber(cl.visits)}</TableCell>
                      <TableCell align="right" sx={{ ...NUM, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {formatPLN(cl.spent)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyHint>Brak klientów w wybranym okresie</EmptyHint>
          )}
        </SectionCard>
      </StatsRow>
    </Stack>
  );
};
